import { Bar, Doughnut } from 'react-chartjs-2'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js'
import { useTranslation } from 'react-i18next'

const formatNbNO = (value: number | string) =>
  Number(value).toLocaleString('nb-NO', { maximumFractionDigits: 0 })

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
)

interface BrandData {
  brand: string
  total_count: number
  ev_percentage?: number
}

interface TopBrandsChartProps {
  data: BrandData[]
  onBrandClick?: (brandName: string) => void
}

export function TopBrandsChart({ data, onBrandClick }: TopBrandsChartProps) {
  const { t } = useTranslation('components')
  const chartData = {
    labels: data.map(item => item.brand),
    datasets: [
      {
        label: t('topBrandsChart.registrations'),
        data: data.map(item => item.total_count),
        backgroundColor: 'rgba(14, 165, 233, 0.7)',
        borderColor: 'rgba(14, 165, 233, 1)',
        borderWidth: 1,
      },
    ],
  }

  const options = {
    indexAxis: 'y' as const,
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      title: {
        display: false,
      },
    },
    scales: {
      x: {
        ticks: {
          callback: (value: number | string) => formatNbNO(value),
        },
      },
    },
    onClick: (_event: any, elements: any[]) => {
      if (elements.length > 0 && onBrandClick) {
        const index = elements[0].index
        const brandName = data[index].brand
        onBrandClick(brandName)
      }
    },
  }

  return (
    <div className="h-[400px]">
      <Bar data={chartData} options={options} />
    </div>
  )
}

interface LeasingChartProps {
  orgLeasingCount: number
  privateLeasingCount: number
  undefinedLeasingCount?: number
  notLeasingCount: number
}

export function LeasingChart({ orgLeasingCount, privateLeasingCount, undefinedLeasingCount = 0, notLeasingCount }: LeasingChartProps) {
  const { t } = useTranslation('components')
  const chartData = {
    labels: [t('leasingChart.business'), t('leasingChart.private'), t('leasingChart.undefined'), t('leasingChart.notLeasing')],
    datasets: [
      {
        data: [orgLeasingCount, privateLeasingCount, undefinedLeasingCount, notLeasingCount],
        backgroundColor: [
          'rgba(139, 92, 246, 0.7)',    // Purple - Org leasing
          'rgba(16, 185, 129, 0.7)',    // Green - Private leasing
          'rgba(249, 115, 22, 0.7)',    // Orange - Undefined leasing
          'rgba(229, 231, 235, 0.7)',   // Light gray - Not leasing
        ],
        borderColor: [
          'rgba(139, 92, 246, 1)',
          'rgba(16, 185, 129, 1)',
          'rgba(249, 115, 22, 1)',
          'rgba(229, 231, 235, 1)',
        ],
        borderWidth: 1,
      },
    ],
  }

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom' as const,
      },
    },
  }

  return (
    <div className="h-[300px]">
      <Doughnut data={chartData} options={options} />
    </div>
  )
}

interface OwnershipChartProps {
  privateCount: number
  companyCount: number
}

export function OwnershipChart({ privateCount, companyCount }: OwnershipChartProps) {
  const { t } = useTranslation('components')
  const chartData = {
    labels: [t('ownershipChart.private'), t('ownershipChart.company')],
    datasets: [
      {
        data: [privateCount, companyCount],
        backgroundColor: [
          'rgba(16, 185, 129, 0.7)',
          'rgba(249, 115, 22, 0.7)',
        ],
        borderColor: [
          'rgba(16, 185, 129, 1)',
          'rgba(249, 115, 22, 1)',
        ],
        borderWidth: 1,
      },
    ],
  }

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom' as const,
      },
    },
  }

  return (
    <div className="h-[300px]">
      <Doughnut data={chartData} options={options} />
    </div>
  )
}
